import { useState, useEffect, useRef } from "react";
import { useNavigate } from "react-router-dom";
import { useAuth } from "@/contexts/AuthContext";
import api from "@/lib/api";
import { ADMIN } from "@/constants/testIds";
import { Building2, Briefcase, FolderKanban, Home, Image, MessageSquare, Settings, LogOut, Plus, Trash2, Save, Pencil, X, Upload, ExternalLink, Share2, FileText, Star, CalendarClock, Mail, LayoutDashboard, Globe, Check, Sparkles } from "lucide-react";
import { toast } from "sonner";

const TABS = [
  { id: "dashboard", label: "Tableau de bord", icon: LayoutDashboard, testId: "admin-tab-dashboard" },
  { id: "company", label: "Société", icon: Building2, testId: ADMIN.tabCompany },
  { id: "services", label: "Prestations", icon: Briefcase, testId: ADMIN.tabServices },
  { id: "projects", label: "Projets", icon: FolderKanban, testId: ADMIN.tabProjects },
  { id: "availabilities", label: "Disponibilités", icon: Home, testId: ADMIN.tabAvailabilities },
  { id: "gallery", label: "Galerie", icon: Image, testId: ADMIN.tabGallery },
  { id: "testimonials", label: "Témoignages", icon: Star, testId: "admin-tab-testimonials" },
  { id: "legal", label: "Pages légales", icon: FileText, testId: "admin-tab-legal" },
  { id: "messages", label: "Messages", icon: MessageSquare, testId: ADMIN.tabMessages },
  { id: "visits", label: "Demandes de visite", icon: CalendarClock, testId: "admin-tab-visits" },
  { id: "emails", label: "Emails (file)", icon: Mail, testId: "admin-tab-emails" },
  { id: "settings", label: "Paramètres", icon: Settings, testId: ADMIN.tabSettings },
];

export default function AdminDashboard() {
  const { user, logout } = useAuth();
  const navigate = useNavigate();
  const [tab, setTab] = useState("dashboard");

  const onLogout = async () => {
    await logout();
    navigate("/admin/login");
  };

  return (
    <div className="min-h-screen admin-bg flex">
      <aside className="w-64 bg-[#0A0A0A] text-white min-h-screen flex flex-col sticky top-0 h-screen">
        <div className="p-6 border-b border-white/10">
          <div className="font-serif text-xl">AMAR SAHRAOUI</div>
          <div className="overline text-[#93C5FD] mt-1 text-[0.6rem]">Administration</div>
        </div>
        <nav className="flex-1 py-4">
          {TABS.map((t) => {
            const Icon = t.icon;
            return (
              <button
                key={t.id}
                onClick={() => setTab(t.id)}
                data-testid={t.testId}
                className={`w-full flex items-center gap-3 px-6 py-3 text-sm transition border-l-2 ${tab === t.id ? "bg-white/5 border-[#1D4ED8] text-white" : "border-transparent text-white/60 hover:text-white hover:bg-white/5"}`}
              >
                <Icon className="w-4 h-4" strokeWidth={1.6} />
                {t.label}
              </button>
            );
          })}
        </nav>
        <div className="p-4 border-t border-white/10">
          <div className="text-xs text-white/50 mb-3 truncate">{user?.email}</div>
          <button onClick={onLogout} data-testid={ADMIN.logout} className="w-full flex items-center justify-center gap-2 py-2.5 border border-white/20 hover:bg-white hover:text-[#0A0A0A] transition text-sm uppercase tracking-wider">
            <LogOut className="w-4 h-4" /> Déconnexion
          </button>
        </div>
      </aside>

      <div className="flex-1 p-8 max-w-[1200px]">
        {tab === "dashboard" && <DashboardTab onNavigate={setTab} />}
        {tab === "company" && <CompanyTab />}
        {tab === "services" && <CrudTab key="services" coll="services" titleField="title" labelSingular="Prestation" labelPlural="Prestations" />}
        {tab === "projects" && <CrudTab key="projects" coll="projects" titleField="name" labelSingular="Projet" labelPlural="Projets" />}
        {tab === "availabilities" && <CrudTab key="availabilities" coll="availabilities" titleField="reference" labelSingular="Disponibilité" labelPlural="Disponibilités" />}
        {tab === "gallery" && <CrudTab key="gallery" coll="gallery" titleField="title" labelSingular="Élément" labelPlural="Galerie" />}
        {tab === "testimonials" && <CrudTab key="testimonials" coll="testimonials" titleField="name" labelSingular="Témoignage" labelPlural="Témoignages" />}
        {tab === "legal" && <LegalTab />}
        {tab === "messages" && <MessagesTab />}
        {tab === "visits" && <VisitsTab />}
        {tab === "emails" && <EmailsTab />}
        {tab === "settings" && <SettingsTab />}
      </div>
    </div>
  );
}

/* ---------------- Company Tab ---------------- */
function DashboardTab({ onNavigate }) {
  const [stats, setStats] = useState(null);
  useEffect(() => {
    api.get("/admin/stats").then((r) => setStats(r.data)).catch(() => {});
  }, []);

  if (!stats) return <div>Chargement…</div>;

  const cards = [
    { label: "Projets publiés", value: `${stats.projects.published} / ${stats.projects.total}`, color: "#1D4ED8", goto: "projects" },
    { label: "Disponibilités", value: stats.availabilities.available, sub: `${stats.availabilities.reserved} réservés · ${stats.availabilities.sold} vendus`, color: "#1D4ED8", goto: "availabilities" },
    { label: "Demandes de visite", value: stats.visits.new, sub: `${stats.visits.this_month} ce mois · ${stats.visits.total} total`, color: "#0A0A0A", goto: "visits" },
    { label: "Messages reçus", value: stats.messages.this_month, sub: `${stats.messages.total} au total`, color: "#0A0A0A", goto: "messages" },
    { label: "Emails en attente", value: stats.emails.pending, sub: "File d'envoi automatique", color: "#1D4ED8", goto: "emails" },
  ];

  return (
    <div>
      <Header title="Tableau de bord" desc="Vue d'ensemble de l'activité du site." />
      <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-3 gap-4">
        {cards.map((c) => (
          <button
            key={c.label}
            onClick={() => onNavigate(c.goto)}
            data-testid={`dashboard-card-${c.goto}`}
            className="text-left bg-white border border-neutral-200 p-6 lg:p-8 hover:border-black transition group"
          >
            <div className="overline text-neutral-500 mb-3 group-hover:text-[#1D4ED8] transition">{c.label}</div>
            <div className="font-serif text-5xl lg:text-6xl mb-2" style={{ color: c.color }}>{c.value}</div>
            {c.sub && <div className="text-xs text-neutral-500">{c.sub}</div>}
          </button>
        ))}
      </div>
    </div>
  );
}

function CompanyTab() {
  const [data, setData] = useState(null);
  const [saving, setSaving] = useState(false);

  useEffect(() => {
    api.get("/public/company").then((r) => setData(r.data || {}));
  }, []);

  if (!data) return <div>Chargement…</div>;

  const save = async () => {
    setSaving(true);
    try {
      await api.put("/admin/company", data);
      toast.success("Société mise à jour");
    } catch (e) {
      toast.error("Erreur lors de l'enregistrement");
    } finally {
      setSaving(false);
    }
  };

  const set = (k) => (v) => setData({ ...data, [k]: v });
  const setKF = (i, k, v) => {
    const kfs = [...(data.key_figures || [])];
    kfs[i] = { ...kfs[i], [k]: v };
    setData({ ...data, key_figures: kfs });
  };
  const setValue = (i, k, v) => {
    const vs = [...(data.values || [])];
    vs[i] = { ...vs[i], [k]: v };
    setData({ ...data, values: vs });
  };

  return (
    <div>
      <Header title="Société" desc="Informations institutionnelles affichées sur le site." />
      <div className="bg-white border border-neutral-200 p-6 lg:p-8 space-y-5">
        <Field label="Nom de l'entreprise" value={data.name} onChange={set("name")} />
        <Field label="Slogan" value={data.slogan} onChange={set("slogan")} />
        <TextArea label="Présentation courte" value={data.short_intro} onChange={set("short_intro")} rows={3} />
        <TextArea label="À propos (long)" value={data.long_about} onChange={set("long_about")} rows={6} />
        <Field label="Histoire" value={data.history} onChange={set("history")} />
        <Field label="Vision" value={data.vision} onChange={set("vision")} />
        <Field label="Mission" value={data.mission} onChange={set("mission")} />
        <TextArea label="Mot de la direction" value={data.director_word} onChange={set("director_word")} rows={3} />
        <Field label="Nom du directeur" value={data.director_name} onChange={set("director_name")} />
        <ImagePicker label="Photo du directeur" value={data.director_photo} onChange={set("director_photo")} />
        <Field label="Expertise" value={data.expertise} onChange={set("expertise")} />
        <ListField label="Zones d'intervention (virgule)" value={(data.zones || []).join(", ")} onChange={(v) => setData({ ...data, zones: v.split(",").map((s) => s.trim()).filter(Boolean) })} />
        <ImagePicker label="Logo URL" value={data.logo_url} onChange={set("logo_url")} />
        <ImagePicker label="Image hero (accueil)" value={data.hero_image_url} onChange={set("hero_image_url")} />

        <div>
          <div className="overline text-neutral-500 mb-3">Valeurs</div>
          {(data.values || []).map((v, i) => (
            <div key={i} className="grid grid-cols-12 gap-3 mb-2">
              <input className="col-span-3 border border-neutral-300 px-3 py-2 text-sm" value={v.title || ""} onChange={(e) => setValue(i, "title", e.target.value)} placeholder="Titre" />
              <input className="col-span-8 border border-neutral-300 px-3 py-2 text-sm" value={v.desc || ""} onChange={(e) => setValue(i, "desc", e.target.value)} placeholder="Description" />
              <button onClick={() => setData({ ...data, values: data.values.filter((_, j) => j !== i) })} className="col-span-1 border border-neutral-300 hover:border-red-500 hover:text-red-500"><Trash2 className="w-4 h-4 mx-auto" /></button>
            </div>
          ))}
          <button onClick={() => setData({ ...data, values: [...(data.values || []), { title: "", desc: "" }] })} className="text-sm text-[#1D4ED8] inline-flex items-center gap-1 mt-2"><Plus className="w-4 h-4" /> Ajouter une valeur</button>
        </div>

        <div>
          <div className="overline text-neutral-500 mb-3">Chiffres clés</div>
          {(data.key_figures || []).map((kf, i) => (
            <div key={i} className="grid grid-cols-12 gap-3 mb-2">
              <input className="col-span-3 border border-neutral-300 px-3 py-2 text-sm" value={kf.value || ""} onChange={(e) => setKF(i, "value", e.target.value)} placeholder="Valeur" />
              <input className="col-span-8 border border-neutral-300 px-3 py-2 text-sm" value={kf.label || ""} onChange={(e) => setKF(i, "label", e.target.value)} placeholder="Libellé" />
              <button onClick={() => setData({ ...data, key_figures: data.key_figures.filter((_, j) => j !== i) })} className="col-span-1 border border-neutral-300 hover:border-red-500 hover:text-red-500"><Trash2 className="w-4 h-4 mx-auto" /></button>
            </div>
          ))}
          <button onClick={() => setData({ ...data, key_figures: [...(data.key_figures || []), { value: "", label: "" }] })} className="text-sm text-[#1D4ED8] inline-flex items-center gap-1 mt-2"><Plus className="w-4 h-4" /> Ajouter un chiffre</button>
        </div>

        <button onClick={save} disabled={saving} className="btn-primary mt-4" data-testid="admin-company-save"><Save className="w-4 h-4" /> {saving ? "Enregistrement…" : "Enregistrer"}</button>
      </div>
    </div>
  );
}

/* ---------------- Generic CRUD Tab ---------------- */
const SCHEMAS = {
  services: [
    { key: "title", label: "Titre", type: "text" },
    { key: "subtitle", label: "Sous-titre", type: "text" },
    { key: "short_description", label: "Description courte", type: "textarea" },
    { key: "long_description", label: "Description longue", type: "textarea", rows: 6 },
    { key: "image_url", label: "Image principale", type: "image" },
    { key: "icon", label: "Icône (Building2, ClipboardList, Compass, Handshake, Users, TrendingUp)", type: "text" },
    { key: "advantages", label: "Avantages (un par ligne)", type: "list" },
    { key: "zone", label: "Zone d'intervention", type: "text" },
    { key: "order", label: "Ordre", type: "number" },
    { key: "featured", label: "Mis en avant", type: "bool" },
    { key: "published", label: "Publié", type: "bool" },
    { key: "seo_title", label: "SEO Title", type: "text" },
    { key: "seo_description", label: "SEO Description", type: "textarea" },
  ],
  projects: [
    { key: "name", label: "Nom du projet", type: "text" },
    { key: "category", label: "Catégorie", type: "select", options: ["residentiel", "commercial", "mixte"] },
    { key: "status", label: "Statut", type: "select", options: ["realise", "en_cours", "futur"] },
    { key: "program", label: "Programme immobilier", type: "text" },
    { key: "short_description", label: "Description courte", type: "textarea" },
    { key: "long_description", label: "Description longue", type: "textarea", rows: 6 },
    { key: "city", label: "Ville", type: "text" },
    { key: "wilaya", label: "Wilaya", type: "text" },
    { key: "address", label: "Adresse", type: "text" },
    { key: "gps_lat", label: "Latitude GPS (auto-génère la carte OpenStreetMap si remplie)", type: "number" },
    { key: "gps_lng", label: "Longitude GPS", type: "number" },
    { key: "map_embed_url", label: "URL embed Google Maps (optionnel — coller l'URL Share→Embed depuis Google Maps, doit contenir /maps/embed/. Laisser vide pour utiliser OpenStreetMap auto)", type: "text" },
    { key: "cover_image", label: "Image de couverture", type: "image" },
    { key: "gallery", label: "Galerie photos & vidéos", type: "image_gallery" },
    { key: "videos", label: "Vidéos additionnelles (URLs externes : YouTube, Vimeo, MP4 direct)", type: "list" },
    { key: "launch_date", label: "Date de lancement", type: "text" },
    { key: "delivery_date", label: "Date de livraison", type: "text" },
    { key: "progress_percent", label: "Avancement (%)", type: "number" },
    { key: "typologies", label: "Typologies (F2, F3…)", type: "list" },
    { key: "surfaces", label: "Surfaces", type: "list" },
    { key: "units_count", label: "Nombre de logements", type: "number" },
    { key: "amenities", label: "Équipements", type: "list" },
    { key: "highlights", label: "Points forts", type: "list" },
    { key: "brochure_url", label: "URL brochure PDF", type: "image" },
    { key: "featured", label: "Mis en avant", type: "bool" },
    { key: "order", label: "Ordre", type: "number" },
    { key: "published", label: "Publié", type: "bool" },
    { key: "seo_title", label: "SEO Title", type: "text" },
    { key: "seo_description", label: "SEO Description", type: "textarea" },
  ],
  availabilities: [
    { key: "project_id", label: "ID du projet associé", type: "project_select" },
    { key: "type", label: "Type", type: "select", options: ["appartement", "villa", "local", "terrain"] },
    { key: "reference", label: "Référence", type: "text" },
    { key: "surface", label: "Surface", type: "text" },
    { key: "rooms", label: "Pièces", type: "number" },
    { key: "floor", label: "Étage", type: "text" },
    { key: "orientation", label: "Orientation", type: "text" },
    { key: "price", label: "Prix", type: "text" },
    { key: "status", label: "Statut", type: "select", options: ["disponible", "reserve", "vendu"] },
    { key: "description", label: "Description", type: "textarea" },
    { key: "image_url", label: "Image", type: "image" },
    { key: "order", label: "Ordre", type: "number" },
    { key: "published", label: "Publié", type: "bool" },
  ],
  gallery: [
    { key: "title", label: "Titre", type: "text" },
    { key: "image_url", label: "Photo ou vidéo", type: "image" },
    { key: "category", label: "Catégorie", type: "text" },
    { key: "order", label: "Ordre", type: "number" },
    { key: "published", label: "Publié", type: "bool" },
  ],
  testimonials: [
    { key: "name", label: "Nom", type: "text" },
    { key: "role", label: "Rôle / projet", type: "text" },
    { key: "content", label: "Témoignage", type: "textarea" },
    { key: "rating", label: "Note (1-5)", type: "number" },
    { key: "avatar", label: "Avatar (URL)", type: "image" },
    { key: "order", label: "Ordre", type: "number" },
    { key: "published", label: "Publié", type: "bool" },
  ],
};

function emptyDoc(coll) {
  const obj = {};
  for (const f of SCHEMAS[coll]) {
    if (f.type === "list") obj[f.key] = [];
    else if (f.type === "bool") obj[f.key] = true;
    else if (f.type === "number") obj[f.key] = 0;
    else obj[f.key] = "";
  }
  if (coll === "projects") {
    obj.published = true;
    obj.featured = false;
    obj.progress_percent = 0;
    obj.units_count = null;
  }
  return obj;
}

function CrudTab({ coll, titleField, labelSingular, labelPlural }) {
  const [items, setItems] = useState([]);
  const [editing, setEditing] = useState(null); // doc or null
  const [projects, setProjects] = useState([]);
  const [loading, setLoading] = useState(true);

  const load = async () => {
    setLoading(true);
    try {
      const { data } = await api.get(`/admin/${coll}`);
      setItems(data);
    } finally {
      setLoading(false);
    }
  };

  useEffect(() => {
    load();
    if (coll === "availabilities") api.get("/admin/projects").then((r) => setProjects(r.data));
  }, [coll]);

  const remove = async (id) => {
    if (!confirm("Supprimer cet élément ?")) return;
    try {
      await api.delete(`/admin/${coll}/${id}`);
      toast.success("Supprimé");
      load();
    } catch (e) {
      toast.error("Erreur");
    }
  };

  const onSave = async (doc) => {
    try {
      if (doc.id) {
        const { id, ...payload } = doc;
        await api.put(`/admin/${coll}/${id}`, payload);
      } else {
        await api.post(`/admin/${coll}`, doc);
      }
      toast.success("Enregistré");
      setEditing(null);
      load();
    } catch (e) {
      toast.error("Erreur lors de l'enregistrement");
    }
  };

  return (
    <div>
      <Header
        title={labelPlural}
        desc={`Gestion des ${labelPlural.toLowerCase()}.`}
        action={<button onClick={() => setEditing(emptyDoc(coll))} className="btn-primary" data-testid={`admin-${coll}-new`}><Plus className="w-4 h-4" /> Nouveau</button>}
      />
      {loading ? <div>Chargement…</div> : (
        <div className="bg-white border border-neutral-200">
          <table className="w-full text-sm">
            <thead className="border-b border-neutral-200 text-left text-xs uppercase tracking-wider text-neutral-500">
              <tr>
                <th className="p-4">Titre</th>
                <th className="p-4">Statut</th>
                <th className="p-4">Ordre</th>
                <th className="p-4 text-right">Actions</th>
              </tr>
            </thead>
            <tbody>
              {items.map((it) => (
                <tr key={it.id} className="border-b border-neutral-100 hover:bg-neutral-50" data-testid={`admin-${coll}-row-${it.id}`}>
                  <td className="p-4">
                    <div className="font-medium">{it[titleField] || "(sans titre)"}</div>
                    {coll === "projects" && <div className="text-xs text-neutral-500">{it.status} · {it.city}</div>}
                  </td>
                  <td className="p-4">
                    <span className={`pill text-xs ${it.published ? "text-emerald-700" : "text-neutral-400"}`}>{it.published ? "Publié" : "Brouillon"}</span>
                    {it.featured && <span className="pill text-xs text-[#1D4ED8] ml-2">★</span>}
                  </td>
                  <td className="p-4">{it.order}</td>
                  <td className="p-4 text-right">
                    <button onClick={() => setEditing(it)} className="p-2 hover:text-[#1D4ED8]" data-testid={`admin-${coll}-edit-${it.id}`}><Pencil className="w-4 h-4" /></button>
                    <button onClick={() => remove(it.id)} className="p-2 hover:text-red-500" data-testid={`admin-${coll}-delete-${it.id}`}><Trash2 className="w-4 h-4" /></button>
                  </td>
                </tr>
              ))}
              {items.length === 0 && <tr><td colSpan={4} className="p-12 text-center text-neutral-500">Aucun élément.</td></tr>}
            </tbody>
          </table>
        </div>
      )}

      {editing && (
        <EditModal
          schema={SCHEMAS[coll]}
          doc={editing}
          onClose={() => setEditing(null)}
          onSave={onSave}
          projects={projects}
          coll={coll}
        />
      )}
    </div>
  );
}

function EditModal({ schema, doc, onClose, onSave, projects, coll }) {
  const [form, setForm] = useState(doc);
  const [activeLang, setActiveLang] = useState("fr");
  const [translating, setTranslating] = useState(false);
  const [translatedAt, setTranslatedAt] = useState(null);
  const set = (k) => (v) => setForm({ ...form, [k]: v });
  const setTranslation = (lang, k) => (v) => {
    const tr = { ...(form.translations || {}) };
    tr[lang] = { ...(tr[lang] || {}), [k]: v };
    setForm({ ...form, translations: tr });
  };
  const getTranslation = (lang, k) => (form.translations || {})[lang]?.[k] || "";

  const translatableFields = TRANSLATABLE_FIELDS[coll] || [];
  const hasTranslations = translatableFields.length > 0;

  const autoTranslate = async () => {
    if (activeLang === "fr") return;
    const fields = {};
    for (const f of translatableFields) {
      const fr = form[f.key];
      if (fr && String(fr).trim()) fields[f.key] = String(fr);
    }
    if (Object.keys(fields).length === 0) {
      toast.error("Aucun contenu français à traduire.");
      return;
    }
    setTranslating(true);
    try {
      const ctx = coll === "projects" ? "Premium real-estate project in Oran, Algeria" : "Premium real-estate service";
      const { data } = await api.post("/admin/translate", { fields, target_lang: activeLang, context: ctx });
      const tr = { ...(form.translations || {}) };
      tr[activeLang] = { ...(tr[activeLang] || {}), ...data.translations };
      setForm({ ...form, translations: tr });
      setTranslatedAt(activeLang);
      toast.success(`Traduction IA générée (${activeLang.toUpperCase()}) — cliquez Enregistrer pour la sauvegarder`);
    } catch (e) {
      toast.error("Erreur de traduction IA : " + (e.response?.data?.detail || e.message));
    } finally {
      setTranslating(false);
    }
  };

  return (
    <div className="fixed inset-0 z-50 bg-black/60 flex items-start justify-center p-4 overflow-auto" onClick={onClose}>
      <div className="bg-white max-w-3xl w-full my-8 border border-neutral-200" onClick={(e) => e.stopPropagation()}>
        <div className="flex items-center justify-between p-6 border-b border-neutral-200">
          <h3 className="font-serif text-2xl">{form.id ? "Modifier" : "Créer"}</h3>
          <button onClick={onClose}><X className="w-5 h-5" /></button>
        </div>

        {hasTranslations && (
          <div className="border-b border-neutral-200 bg-neutral-50">
            <div className="px-6 lg:px-8 flex items-center gap-1 flex-wrap">
              {[
                { code: "fr", label: "🇫🇷 Français" },
                { code: "ar", label: "🇩🇿 العربية" },
                { code: "en", label: "🇬🇧 English" },
              ].map((l) => (
                <button
                  key={l.code}
                  onClick={() => setActiveLang(l.code)}
                  data-testid={`admin-${coll}-lang-${l.code}`}
                  className={`px-4 py-3 text-xs uppercase tracking-wider border-b-2 transition ${activeLang === l.code ? "border-[#1D4ED8] text-[#1D4ED8]" : "border-transparent text-neutral-500 hover:text-black"}`}
                >
                  {l.label}
                </button>
              ))}
              {activeLang !== "fr" && (
                <button
                  onClick={autoTranslate}
                  disabled={translating}
                  data-testid={`admin-${coll}-ai-translate-${activeLang}`}
                  className="ml-auto inline-flex items-center gap-2 px-4 py-2 text-xs uppercase tracking-wider bg-gradient-to-r from-[#1D4ED8] to-[#1E3A8A] text-white hover:from-black hover:to-black transition disabled:opacity-50"
                >
                  <Sparkles className="w-4 h-4" />
                  {translating ? "Traduction…" : `Traduire avec l'IA → ${activeLang.toUpperCase()}`}
                </button>
              )}
              {activeLang === "fr" && (
                <div className="ml-auto text-xs text-neutral-500 flex items-center gap-2">
                  <Globe className="w-3 h-3" /> Langue principale
                </div>
              )}
            </div>
          </div>
        )}

        <div className="p-6 lg:p-8 space-y-4 max-h-[60vh] overflow-y-auto" dir={activeLang === "ar" ? "rtl" : "ltr"}>
          {translatedAt === activeLang && (
            <div className="bg-[#F0F4FF] border-l-2 border-[#1D4ED8] p-3 text-xs text-[#1E3A8A]" dir="ltr">
              ✨ <strong>Traduction IA appliquée</strong> — relisez les champs ci-dessous puis cliquez <strong>Enregistrer</strong> en bas pour sauvegarder.
            </div>
          )}
          {activeLang === "fr" ? (
            schema.map((f) => (
              <SchemaField key={f.key} field={f} value={form[f.key]} onChange={set(f.key)} projects={projects} setForm={setForm} form={form} />
            ))
          ) : (
            translatableFields.map((f) => (
              <SchemaField
                key={f.key}
                field={f}
                value={getTranslation(activeLang, f.key)}
                onChange={setTranslation(activeLang, f.key)}
                projects={projects}
                setForm={setForm}
                form={form}
              />
            ))
          )}
        </div>

        <div className="p-6 border-t border-neutral-200 flex justify-between gap-3">
          {activeLang !== "fr" && <div className="text-xs text-neutral-500">Astuce : laisse vide pour reprendre la valeur française.</div>}
          <div className="flex gap-3 ml-auto">
            <button onClick={onClose} className="btn-outline">Annuler</button>
            <button onClick={() => onSave(form)} className="btn-primary" data-testid={`admin-${coll}-save`}><Save className="w-4 h-4" /> Enregistrer</button>
          </div>
        </div>
      </div>
    </div>
  );
}

// Fields that support per-language translation
const TRANSLATABLE_FIELDS = {
  services: [
    { key: "title", label: "Titre", type: "text" },
    { key: "subtitle", label: "Sous-titre", type: "text" },
    { key: "short_description", label: "Description courte", type: "textarea" },
    { key: "long_description", label: "Description longue", type: "textarea", rows: 6 },
  ],
  projects: [
    { key: "name", label: "Nom du projet", type: "text" },
    { key: "program", label: "Programme immobilier", type: "text" },
    { key: "short_description", label: "Description courte", type: "textarea" },
    { key: "long_description", label: "Description longue", type: "textarea", rows: 6 },
  ],
};

function SchemaField({ field, value, onChange, projects, form, setForm }) {
  if (field.type === "text") return <Field label={field.label} value={value || ""} onChange={onChange} />;
  if (field.type === "textarea") return <TextArea label={field.label} value={value || ""} onChange={onChange} rows={field.rows || 3} />;
  if (field.type === "number") return <Field label={field.label} type="number" value={value ?? ""} onChange={(v) => onChange(v === "" ? null : Number(v))} />;
  if (field.type === "bool") return (
    <label className="flex items-center gap-3 text-sm py-1">
      <input type="checkbox" checked={!!value} onChange={(e) => onChange(e.target.checked)} className="w-4 h-4 accent-[#1D4ED8]" />
      <span className="overline text-neutral-700">{field.label}</span>
    </label>
  );
  if (field.type === "select") return (
    <div>
      <label className="overline text-neutral-500 mb-2 block">{field.label}</label>
      <select value={value || ""} onChange={(e) => onChange(e.target.value)} className="w-full border border-neutral-300 px-3 py-2.5 text-sm bg-white">
        {field.options.map((o) => <option key={o} value={o}>{o}</option>)}
      </select>
    </div>
  );
  if (field.type === "project_select") return (
    <div>
      <label className="overline text-neutral-500 mb-2 block">{field.label}</label>
      <select value={value || ""} onChange={(e) => { onChange(e.target.value); const proj = projects.find((p) => p.id === e.target.value); if (proj) setForm({ ...form, project_id: e.target.value, project_name: proj.name }); }} className="w-full border border-neutral-300 px-3 py-2.5 text-sm bg-white">
        <option value="">(aucun)</option>
        {projects.map((p) => <option key={p.id} value={p.id}>{p.name}</option>)}
      </select>
    </div>
  );
  if (field.type === "list") return <ListMultiline label={field.label} value={value || []} onChange={onChange} />;
  if (field.type === "image_gallery") return <ImageGalleryPicker label={field.label} value={value || []} onChange={onChange} />;
  if (field.type === "image") return <ImagePicker label={field.label} value={value || ""} onChange={onChange} />;
  return null;
}

/* ---------------- Legal Tab ---------------- */
function LegalTab() {
  const [pages, setPages] = useState([]);
  const [editing, setEditing] = useState(null);

  const load = async () => {
    const { data } = await api.get("/admin/legal");
    setPages(data);
  };
  useEffect(() => { load(); }, []);

  const save = async () => {
    try {
      await api.put(`/admin/legal/${editing.slug}`, editing);
      toast.success("Enregistré");
      setEditing(null);
      load();
    } catch { toast.error("Erreur"); }
  };

  return (
    <div>
      <Header title="Pages légales" desc="Mentions légales et politique de confidentialité." />
      <div className="bg-white border border-neutral-200">
        {pages.map((p) => (
          <div key={p.slug} className="flex items-center justify-between p-5 border-b border-neutral-100 last:border-0">
            <div>
              <div className="font-serif text-lg">{p.title}</div>
              <div className="text-xs text-neutral-500">/{p.slug}</div>
            </div>
            <button onClick={() => setEditing(p)} className="btn-outline"><Pencil className="w-4 h-4" /> Modifier</button>
          </div>
        ))}
      </div>

      {editing && (
        <div className="fixed inset-0 z-50 bg-black/60 flex items-start justify-center p-4 overflow-auto" onClick={() => setEditing(null)}>
          <div className="bg-white max-w-3xl w-full my-8 border border-neutral-200" onClick={(e) => e.stopPropagation()}>
            <div className="flex items-center justify-between p-6 border-b border-neutral-200">
              <h3 className="font-serif text-2xl">Modifier {editing.title}</h3>
              <button onClick={() => setEditing(null)}><X className="w-5 h-5" /></button>
            </div>
            <div className="p-6 lg:p-8 space-y-4">
              <Field label="Titre" value={editing.title} onChange={(v) => setEditing({ ...editing, title: v })} />
              <TextArea label="Contenu HTML" value={editing.content} onChange={(v) => setEditing({ ...editing, content: v })} rows={20} />
            </div>
            <div className="p-6 border-t flex justify-end gap-3">
              <button onClick={() => setEditing(null)} className="btn-outline">Annuler</button>
              <button onClick={save} className="btn-primary"><Save className="w-4 h-4" /> Enregistrer</button>
            </div>
          </div>
        </div>
      )}
    </div>
  );
}

/* ---------------- Messages Tab ---------------- */
function MessagesTab() {
  const [msgs, setMsgs] = useState([]);
  const [open, setOpen] = useState(null);

  const load = async () => {
    const { data } = await api.get("/admin/messages");
    setMsgs(data);
  };
  useEffect(() => { load(); }, []);

  const remove = async (id) => {
    if (!confirm("Supprimer ce message ?")) return;
    await api.delete(`/admin/messages/${id}`);
    toast.success("Supprimé");
    load();
  };

  return (
    <div>
      <Header title="Messages reçus" desc="Demandes envoyées via le formulaire de contact." />
      <div className="bg-white border border-neutral-200">
        <table className="w-full text-sm">
          <thead className="border-b border-neutral-200 text-left text-xs uppercase tracking-wider text-neutral-500">
            <tr>
              <th className="p-4">Date</th><th className="p-4">Nom</th><th className="p-4">Email</th><th className="p-4">Sujet</th><th className="p-4 text-right">Actions</th>
            </tr>
          </thead>
          <tbody>
            {msgs.map((m) => (
              <tr key={m.id} className="border-b border-neutral-100 hover:bg-neutral-50">
                <td className="p-4 text-xs">{m.created_at?.split("T")[0]}</td>
                <td className="p-4">{m.first_name} {m.last_name}</td>
                <td className="p-4">{m.email}</td>
                <td className="p-4 truncate max-w-[260px]">{m.subject || "—"}</td>
                <td className="p-4 text-right">
                  <button onClick={() => setOpen(m)} className="p-2 hover:text-[#1D4ED8]"><ExternalLink className="w-4 h-4" /></button>
                  <button onClick={() => remove(m.id)} className="p-2 hover:text-red-500"><Trash2 className="w-4 h-4" /></button>
                </td>
              </tr>
            ))}
            {msgs.length === 0 && <tr><td colSpan={5} className="p-12 text-center text-neutral-500">Aucun message.</td></tr>}
          </tbody>
        </table>
      </div>

      {open && (
        <div className="fixed inset-0 z-50 bg-black/60 flex items-start justify-center p-4" onClick={() => setOpen(null)}>
          <div className="bg-white max-w-xl w-full my-12 p-8 border border-neutral-200" onClick={(e) => e.stopPropagation()}>
            <div className="flex justify-between items-start mb-6">
              <div>
                <div className="overline text-[#1D4ED8] mb-1">Message</div>
                <h3 className="font-serif text-2xl">{open.first_name} {open.last_name}</h3>
              </div>
              <button onClick={() => setOpen(null)}><X className="w-5 h-5" /></button>
            </div>
            <div className="space-y-3 text-sm">
              <div><span className="overline text-neutral-500">Email :</span> <a href={`mailto:${open.email}`} className="text-[#1D4ED8]">{open.email}</a></div>
              {open.phone && <div><span className="overline text-neutral-500">Téléphone :</span> {open.phone}</div>}
              {open.subject && <div><span className="overline text-neutral-500">Sujet :</span> {open.subject}</div>}
              {open.project_name && <div><span className="overline text-neutral-500">Projet :</span> {open.project_name}</div>}
              <div className="pt-3 border-t border-neutral-100"><div className="overline text-neutral-500 mb-2">Message</div><div className="whitespace-pre-line text-neutral-800">{open.message}</div></div>
            </div>
          </div>
        </div>
      )}
    </div>
  );
}

/* ---------------- Settings Tab ---------------- */
function VisitsTab() {
  const [items, setItems] = useState([]);
  const [open, setOpen] = useState(null);

  const load = async () => {
    const { data } = await api.get("/admin/visit-requests");
    setItems(data);
  };
  useEffect(() => { load(); }, []);

  const remove = async (id) => {
    if (!confirm("Supprimer cette demande ?")) return;
    await api.delete(`/admin/visit-requests/${id}`);
    toast.success("Supprimé");
    load();
  };

  const updateStatus = async (id, status) => {
    await api.put(`/admin/visit-requests/${id}`, { status });
    toast.success("Statut mis à jour");
    load();
  };

  const labelType = (t) => t === "on_site" ? "Sur place" : t === "virtual" ? "Virtuelle" : t === "call_back" ? "Rappel" : t;
  const labelStatus = (s) => s === "new" ? "Nouvelle" : s === "confirmed" ? "Confirmée" : s === "done" ? "Effectuée" : s === "cancelled" ? "Annulée" : s;

  return (
    <div>
      <Header title="Demandes de visite" desc="Rendez-vous physiques, virtuels et rappels demandés via le site." />
      <div className="bg-white border border-neutral-200 overflow-x-auto">
        <table className="w-full text-sm">
          <thead className="border-b border-neutral-200 text-left text-xs uppercase tracking-wider text-neutral-500">
            <tr>
              <th className="p-4">Date</th>
              <th className="p-4">Demandeur</th>
              <th className="p-4">Projet</th>
              <th className="p-4">Type</th>
              <th className="p-4">Souhait</th>
              <th className="p-4">Statut</th>
              <th className="p-4 text-right">Actions</th>
            </tr>
          </thead>
          <tbody>
            {items.map((m) => (
              <tr key={m.id} className="border-b border-neutral-100 hover:bg-neutral-50" data-testid={`admin-visit-row-${m.id}`}>
                <td className="p-4 text-xs">{m.created_at?.split("T")[0]}</td>
                <td className="p-4">
                  <div className="font-medium">{m.first_name} {m.last_name}</div>
                  <div className="text-xs text-neutral-500">{m.phone}</div>
                </td>
                <td className="p-4">{m.project_name || "—"}</td>
                <td className="p-4"><span className="pill text-xs text-[#1D4ED8]">{labelType(m.visit_type)}</span></td>
                <td className="p-4 text-xs">{m.preferred_date || "—"} <span className="text-neutral-400">·</span> {m.preferred_time || "—"}</td>
                <td className="p-4">
                  <select value={m.status || "new"} onChange={(e) => updateStatus(m.id, e.target.value)} className="border border-neutral-300 px-2 py-1 text-xs bg-white" data-testid={`admin-visit-status-${m.id}`}>
                    <option value="new">{labelStatus("new")}</option>
                    <option value="confirmed">{labelStatus("confirmed")}</option>
                    <option value="done">{labelStatus("done")}</option>
                    <option value="cancelled">{labelStatus("cancelled")}</option>
                  </select>
                </td>
                <td className="p-4 text-right">
                  <button onClick={() => setOpen(m)} className="p-2 hover:text-[#1D4ED8]"><ExternalLink className="w-4 h-4" /></button>
                  <button onClick={() => remove(m.id)} className="p-2 hover:text-red-500"><Trash2 className="w-4 h-4" /></button>
                </td>
              </tr>
            ))}
            {items.length === 0 && <tr><td colSpan={7} className="p-12 text-center text-neutral-500">Aucune demande de visite.</td></tr>}
          </tbody>
        </table>
      </div>

      {open && (
        <div className="fixed inset-0 z-50 bg-black/60 flex items-start justify-center p-4" onClick={() => setOpen(null)}>
          <div className="bg-white max-w-xl w-full my-12 p-8 border border-neutral-200" onClick={(e) => e.stopPropagation()}>
            <div className="flex justify-between items-start mb-6">
              <div>
                <div className="overline text-[#1D4ED8] mb-1">Demande de visite</div>
                <h3 className="font-serif text-2xl">{open.first_name} {open.last_name}</h3>
              </div>
              <button onClick={() => setOpen(null)}><X className="w-5 h-5" /></button>
            </div>
            <div className="space-y-3 text-sm">
              <Detail label="Email" value={<a href={`mailto:${open.email}`} className="text-[#1D4ED8]">{open.email}</a>} />
              <Detail label="Téléphone" value={<a href={`tel:${open.phone}`} className="text-[#1D4ED8]">{open.phone}</a>} />
              <Detail label="Projet" value={open.project_name || "—"} />
              <Detail label="Type de visite" value={labelType(open.visit_type)} />
              <Detail label="Date souhaitée" value={open.preferred_date || "—"} />
              <Detail label="Créneau" value={open.preferred_time || "—"} />
              {open.notes && (
                <div className="pt-3 border-t border-neutral-100">
                  <div className="overline text-neutral-500 mb-2">Notes</div>
                  <div className="whitespace-pre-line text-neutral-800">{open.notes}</div>
                </div>
              )}
            </div>
          </div>
        </div>
      )}
    </div>
  );
}

function Detail({ label, value }) {
  return <div className="flex gap-2"><span className="overline text-neutral-500 w-32 flex-shrink-0">{label} :</span><span>{value}</span></div>;
}

/* ---------------- Emails (queue) Tab ---------------- */
function EmailsTab() {
  const [emails, setEmails] = useState([]);
  const [config, setConfig] = useState(null);
  const [open, setOpen] = useState(null);

  const load = async () => {
    const { data } = await api.get("/admin/email-queue");
    setEmails(data);
  };
  useEffect(() => {
    load();
    api.get("/admin/email-config").then((r) => setConfig(r.data)).catch(() => {});
  }, []);

  const sendNow = async (id) => {
    try {
      await api.post(`/admin/email-queue/${id}/send`);
      toast.success("Email envoyé");
      load();
    } catch (e) {
      toast.error("Échec d'envoi : " + (e.response?.data?.detail || ""));
      load();
    }
  };

  const markSent = async (id) => {
    await api.put(`/admin/email-queue/${id}`, { status: "sent", sent_at: new Date().toISOString() });
    toast.success("Marqué comme envoyé");
    load();
  };

  const remove = async (id) => {
    if (!confirm("Supprimer cet email ?")) return;
    await api.delete(`/admin/email-queue/${id}`);
    toast.success("Supprimé");
    load();
  };

  const statusBadge = (s) => {
    if (s === "sent") return <span className="pill text-emerald-700 text-xs">Envoyé</span>;
    if (s === "failed") return <span className="pill text-red-600 text-xs">Échec</span>;
    return <span className="pill text-amber-600 text-xs">En attente</span>;
  };

  return (
    <div>
      <Header title="File d'envoi des emails" desc="Emails générés automatiquement (confirmation de visite, etc.)." />
      {config && (
        config.configured ? (
          <div className={`bg-white border border-neutral-200 p-4 mb-4 text-xs border-l-2 ${config.sandbox ? "border-l-amber-500" : "border-l-emerald-500"}`}>
            <div className="font-semibold mb-1">{config.sandbox ? "⚠️ Mode SANDBOX Resend" : "✅ Resend actif"}</div>
            <div className="text-neutral-600">
              Expéditeur : <code className="bg-neutral-100 px-1">{config.sender}</code>
              {config.sandbox && (
                <span> — En sandbox, seules les adresses vérifiées dans votre compte Resend reçoivent les emails. <a href="https://resend.com/domains" target="_blank" rel="noreferrer" className="text-[#1D4ED8] underline">Vérifier votre domaine</a> pour activer l'envoi à tous les destinataires.</span>
              )}
            </div>
          </div>
        ) : (
          <div className="bg-white border border-neutral-200 p-4 mb-4 text-xs text-neutral-600 border-l-2 border-l-red-500">
            ❌ <strong>Resend non configuré</strong> — ajoutez RESEND_API_KEY dans /app/backend/.env.
          </div>
        )
      )}
      <div className="bg-white border border-neutral-200">
        <table className="w-full text-sm">
          <thead className="border-b border-neutral-200 text-left text-xs uppercase tracking-wider text-neutral-500">
            <tr>
              <th className="p-4">Créé le</th><th className="p-4">Destinataire</th><th className="p-4">Sujet</th><th className="p-4">Statut</th><th className="p-4 text-right">Actions</th>
            </tr>
          </thead>
          <tbody>
            {emails.map((m) => (
              <tr key={m.id} className="border-b border-neutral-100 hover:bg-neutral-50" data-testid={`admin-email-row-${m.id}`}>
                <td className="p-4 text-xs">{m.created_at?.split("T")[0]}</td>
                <td className="p-4">{m.to}</td>
                <td className="p-4 truncate max-w-[260px]">{m.subject}</td>
                <td className="p-4">{statusBadge(m.status)}{m.error && <div className="text-[10px] text-red-500 mt-1 max-w-[180px] truncate" title={m.error}>{m.error}</div>}</td>
                <td className="p-4 text-right whitespace-nowrap">
                  <button onClick={() => setOpen(m)} className="p-2 hover:text-[#1D4ED8]" title="Voir"><ExternalLink className="w-4 h-4" /></button>
                  {m.status !== "sent" && config?.configured && (
                    <button onClick={() => sendNow(m.id)} className="p-2 hover:text-[#1D4ED8]" title="Envoyer via Resend" data-testid={`admin-email-send-${m.id}`}><Mail className="w-4 h-4" /></button>
                  )}
                  {m.status !== "sent" && (
                    <button onClick={() => markSent(m.id)} className="p-2 hover:text-emerald-600" title="Marquer envoyé" data-testid={`admin-email-mark-${m.id}`}><Check className="w-4 h-4" /></button>
                  )}
                  <button onClick={() => remove(m.id)} className="p-2 hover:text-red-500"><Trash2 className="w-4 h-4" /></button>
                </td>
              </tr>
            ))}
            {emails.length === 0 && <tr><td colSpan={5} className="p-12 text-center text-neutral-500">Aucun email en file pour le moment.</td></tr>}
          </tbody>
        </table>
      </div>

      {open && (
        <div className="fixed inset-0 z-50 bg-black/60 flex items-start justify-center p-4 overflow-auto" onClick={() => setOpen(null)}>
          <div className="bg-white max-w-2xl w-full my-12 p-8 border border-neutral-200" onClick={(e) => e.stopPropagation()}>
            <div className="flex justify-between items-start mb-6">
              <div>
                <div className="overline text-[#1D4ED8] mb-1">Email</div>
                <h3 className="font-serif text-2xl">{open.subject}</h3>
              </div>
              <button onClick={() => setOpen(null)}><X className="w-5 h-5" /></button>
            </div>
            <div className="space-y-3 text-sm">
              <Detail label="De" value={open.from_addr} />
              <Detail label="À" value={open.to} />
              <Detail label="Statut" value={statusBadge(open.status)} />
              <Detail label="Fournisseur" value={open.provider || "—"} />
              {open.error && <Detail label="Erreur" value={<span className="text-red-600">{open.error}</span>} />}
              <div className="pt-3 border-t border-neutral-100">
                <div className="overline text-neutral-500 mb-2">Corps</div>
                <pre className="whitespace-pre-wrap font-sans text-neutral-800 bg-neutral-50 p-4 border border-neutral-200">{open.body}</pre>
              </div>
            </div>
          </div>
        </div>
      )}
    </div>
  );
}

// Add Check import is already in lucide-react imports. Already used above? Need to verify.

function SettingsTab() {
  const [contact, setContact] = useState({});
  const [social, setSocial] = useState({});
  const [seo, setSeo] = useState({});

  useEffect(() => {
    api.get("/public/contact-info").then((r) => setContact(r.data || {}));
    api.get("/public/social-links").then((r) => setSocial(r.data || {}));
    api.get("/public/site-settings").then((r) => setSeo(r.data || {}));
  }, []);

  const saveContact = async () => { await api.put("/admin/contact-info", contact); toast.success("Coordonnées enregistrées"); };
  const saveSocial = async () => { await api.put("/admin/social-links", social); toast.success("Réseaux sociaux enregistrés"); };
  const saveSeo = async () => { await api.put("/admin/site-settings", seo); toast.success("SEO enregistré"); };

  return (
    <div>
      <Header title="Paramètres" desc="Coordonnées, réseaux sociaux et SEO global." />
      <div className="space-y-6">
        <Card title="Coordonnées de contact">
          <Field label="Téléphone" value={contact.phone || ""} onChange={(v) => setContact({ ...contact, phone: v })} />
          <Field label="WhatsApp (chiffres seulement, ex: 213561667041)" value={contact.whatsapp || ""} onChange={(v) => setContact({ ...contact, whatsapp: v })} />
          <Field label="Email" value={contact.email || ""} onChange={(v) => setContact({ ...contact, email: v })} />
          <Field label="Adresse" value={contact.address || ""} onChange={(v) => setContact({ ...contact, address: v })} />
          <Field label="Horaires" value={contact.hours || ""} onChange={(v) => setContact({ ...contact, hours: v })} />
          <Field label="Lien Google Maps embed (iframe src)" value={contact.map_embed_url || ""} onChange={(v) => setContact({ ...contact, map_embed_url: v })} />
          <button onClick={saveContact} className="btn-primary"><Save className="w-4 h-4" /> Enregistrer</button>
        </Card>
        <Card title="Réseaux sociaux">
          <Field label="Facebook" value={social.facebook || ""} onChange={(v) => setSocial({ ...social, facebook: v })} />
          <Field label="Instagram" value={social.instagram || ""} onChange={(v) => setSocial({ ...social, instagram: v })} />
          <Field label="LinkedIn" value={social.linkedin || ""} onChange={(v) => setSocial({ ...social, linkedin: v })} />
          <Field label="YouTube" value={social.youtube || ""} onChange={(v) => setSocial({ ...social, youtube: v })} />
          <Field label="TikTok" value={social.tiktok || ""} onChange={(v) => setSocial({ ...social, tiktok: v })} />
          <Field label="X (Twitter)" value={social.twitter || ""} onChange={(v) => setSocial({ ...social, twitter: v })} />
          <button onClick={saveSocial} className="btn-primary"><Save className="w-4 h-4" /> Enregistrer</button>
        </Card>
        <Card title="SEO global">
          <Field label="Titre par défaut" value={seo.seo_title || ""} onChange={(v) => setSeo({ ...seo, seo_title: v })} />
          <TextArea label="Meta description" value={seo.seo_description || ""} onChange={(v) => setSeo({ ...seo, seo_description: v })} />
          <ImagePicker label="Open Graph image" value={seo.og_image || ""} onChange={(v) => setSeo({ ...seo, og_image: v })} />
          <button onClick={saveSeo} className="btn-primary"><Save className="w-4 h-4" /> Enregistrer</button>
        </Card>
      </div>
    </div>
  );
}

/* ---------------- Reusable inputs ---------------- */
function Header({ title, desc, action }) {
  return (
    <div className="flex items-end justify-between mb-8 gap-4 flex-wrap">
      <div>
        <h2 className="font-serif text-4xl tracking-tight">{title}</h2>
        {desc && <p className="text-neutral-500 mt-1 text-sm">{desc}</p>}
      </div>
      {action}
    </div>
  );
}

function Card({ title, children }) {
  return (
    <div className="bg-white border border-neutral-200 p-6 lg:p-8 space-y-4">
      <h3 className="font-serif text-2xl border-b border-neutral-100 pb-3 mb-4">{title}</h3>
      {children}
    </div>
  );
}

function Field({ label, value, onChange, type = "text" }) {
  return (
    <div>
      <label className="overline text-neutral-500 mb-2 block">{label}</label>
      <input type={type} value={value ?? ""} onChange={(e) => onChange(e.target.value)} className="w-full border border-neutral-300 px-4 py-2.5 text-sm focus:border-black outline-none" />
    </div>
  );
}

function TextArea({ label, value, onChange, rows = 4 }) {
  return (
    <div>
      <label className="overline text-neutral-500 mb-2 block">{label}</label>
      <textarea rows={rows} value={value || ""} onChange={(e) => onChange(e.target.value)} className="w-full border border-neutral-300 px-4 py-2.5 text-sm focus:border-black outline-none" />
    </div>
  );
}

function ListField({ label, value, onChange }) {
  return <Field label={label} value={value} onChange={onChange} />;
}

function ListMultiline({ label, value, onChange }) {
  const text = (value || []).join("\n");
  return (
    <div>
      <label className="overline text-neutral-500 mb-2 block">{label}</label>
      <textarea
        rows={Math.min(8, Math.max(3, (value || []).length + 1))}
        value={text}
        onChange={(e) => onChange(e.target.value.split("\n").map((s) => s.trim()).filter(Boolean))}
        className="w-full border border-neutral-300 px-4 py-2.5 text-sm focus:border-black outline-none"
      />
    </div>
  );
}

function ImagePicker({ label, value, onChange }) {
  const inputRef = useRef(null);
  const [uploading, setUploading] = useState(false);

  const upload = async (file) => {
    if (!file) return;
    setUploading(true);
    try {
      const fd = new FormData();
      fd.append("file", file);
      const { data } = await api.post("/admin/upload", fd, { headers: { "Content-Type": "multipart/form-data" } });
      const url = data.url.startsWith("http") ? data.url : `${process.env.REACT_APP_BACKEND_URL}${data.url}`;
      onChange(url);
      toast.success("Fichier importé");
    } catch (e) {
      toast.error("Erreur d'envoi");
    } finally {
      setUploading(false);
    }
  };

  return (
    <div>
      <label className="overline text-neutral-500 mb-2 block">{label}</label>
      <div className="flex gap-3 items-start">
        <input
          type="text"
          value={value || ""}
          onChange={(e) => onChange(e.target.value)}
          placeholder="URL ou téléverser un fichier"
          className="flex-1 border border-neutral-300 px-4 py-2.5 text-sm focus:border-black outline-none"
        />
        <button type="button" onClick={() => inputRef.current?.click()} disabled={uploading} className="btn-outline whitespace-nowrap"><Upload className="w-4 h-4" />{uploading ? "…" : "Téléverser"}</button>
        <input ref={inputRef} type="file" className="hidden" accept="image/*,video/mp4,video/webm,application/pdf" onChange={(e) => upload(e.target.files?.[0])} />
      </div>
      {value && (value.match(/\.(jpe?g|png|webp|gif)$/i) || value.includes("images.unsplash") || value.includes("images.pexels") || value.startsWith("data:image")) && (
        <img src={value} alt="" className="mt-3 max-h-32 border border-neutral-200" onError={(e) => { e.target.style.display = "none"; }} />
      )}
    </div>
  );
}

function ImageGalleryPicker({ label, value, onChange }) {
  const inputRef = useRef(null);
  const [uploading, setUploading] = useState(false);
  const items = value || [];

  const uploadFiles = async (files) => {
    if (!files || !files.length) return;
    setUploading(true);
    try {
      const uploaded = [];
      for (const file of files) {
        const fd = new FormData();
        fd.append("file", file);
        // eslint-disable-next-line no-await-in-loop
        const { data } = await api.post("/admin/upload", fd, { headers: { "Content-Type": "multipart/form-data" } });
        const url = data.url.startsWith("http") ? data.url : `${process.env.REACT_APP_BACKEND_URL}${data.url}`;
        uploaded.push(url);
      }
      onChange([...items, ...uploaded]);
      toast.success(`${uploaded.length} fichier(s) ajouté(s)`);
    } catch (e) {
      toast.error("Erreur lors de l'envoi d'un fichier");
    } finally {
      setUploading(false);
    }
  };

  const removeAt = (idx) => onChange(items.filter((_, i) => i !== idx));
  const moveUp = (idx) => {
    if (idx === 0) return;
    const arr = [...items];
    [arr[idx - 1], arr[idx]] = [arr[idx], arr[idx - 1]];
    onChange(arr);
  };

  const onDrop = (e) => {
    e.preventDefault();
    if (e.dataTransfer.files?.length) uploadFiles(Array.from(e.dataTransfer.files));
  };

  const isVid = (url) => /\.(mp4|webm|mov|m4v|ogv)(\?|$)/i.test(url || "");

  return (
    <div>
      <label className="overline text-neutral-500 mb-2 block">{label}</label>
      <div
        onDragOver={(e) => e.preventDefault()}
        onDrop={onDrop}
        className="border-2 border-dashed border-neutral-300 hover:border-[#1D4ED8] p-6 text-center transition cursor-pointer"
        onClick={() => inputRef.current?.click()}
        data-testid="admin-gallery-dropzone"
      >
        <Upload className="w-7 h-7 text-[#1D4ED8] mx-auto mb-2" strokeWidth={1.4} />
        <div className="text-sm">
          {uploading ? "Envoi en cours…" : "Cliquer pour sélectionner OU glisser-déposer des photos/vidéos ici"}
        </div>
        <div className="text-xs text-neutral-500 mt-1">JPG, PNG, WebP, MP4, WebM — plusieurs fichiers possibles</div>
        <input
          ref={inputRef}
          type="file"
          multiple
          accept="image/*,video/mp4,video/webm,video/quicktime"
          className="hidden"
          onChange={(e) => uploadFiles(Array.from(e.target.files || []))}
        />
      </div>

      {items.length > 0 && (
        <div className="grid grid-cols-3 sm:grid-cols-4 lg:grid-cols-5 gap-2 mt-3">
          {items.map((url, idx) => (
            <div key={`${url}-${idx}`} className="relative group border border-neutral-200 aspect-square overflow-hidden bg-neutral-100" data-testid={`admin-gallery-item-${idx}`}>
              {isVid(url) ? (
                <>
                  <video src={url} className="w-full h-full object-cover" muted playsInline preload="metadata" />
                  <div className="absolute top-1 right-1 bg-black/70 text-white text-[9px] px-1.5 py-0.5 uppercase tracking-wider">Vidéo</div>
                </>
              ) : (
                <img src={url} alt="" className="w-full h-full object-cover" onError={(e) => { e.target.style.opacity = "0.3"; }} />
              )}
              <div className="absolute inset-0 bg-black/0 group-hover:bg-black/60 transition flex items-center justify-center gap-1 opacity-0 group-hover:opacity-100">
                <button type="button" onClick={(e) => { e.stopPropagation(); moveUp(idx); }} className="bg-white text-black px-2 py-1 text-xs font-semibold" title="Monter">↑</button>
                <button type="button" onClick={(e) => { e.stopPropagation(); removeAt(idx); }} className="bg-red-500 text-white px-2 py-1 text-xs font-semibold" title="Supprimer">×</button>
              </div>
              <div className="absolute top-1 left-1 bg-black/60 text-white text-[10px] px-1.5 py-0.5">{idx + 1}</div>
            </div>
          ))}
        </div>
      )}

      <details className="mt-2">
        <summary className="text-xs text-neutral-500 cursor-pointer hover:text-black">Avancé : éditer les URLs manuellement</summary>
        <textarea
          rows={Math.min(6, items.length + 1)}
          value={items.join("\n")}
          onChange={(e) => onChange(e.target.value.split("\n").map((s) => s.trim()).filter(Boolean))}
          className="w-full mt-2 border border-neutral-300 px-3 py-2 text-xs font-mono focus:border-black outline-none"
          placeholder="https://… (une URL par ligne, photos ou vidéos)"
        />
      </details>
    </div>
  );
}
